import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/logger";

const AuthContext = createContext(null);

// httpOnly-cookie based session. We NEVER store the auth token on the client.
// Session hydration is done by calling /auth/me on mount — if the cookie is
// present and valid the backend returns the user; otherwise 401 → anon.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) setUser(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    // Backend sets httpOnly access/refresh cookies on this response.
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    // Backend sets httpOnly access/refresh cookies on this response.
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side cookie clear. If it fails (network / stale
    // session / server 5xx), we still force the local state to signed-out
    // so the user is never trapped. Log the failure only in dev builds.
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logDev("auth.logout", err);
    }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
